/**
 * CreateBookingModal — the CSR "New booking" modal (BON-142, designs 01/03).
 * Orchestrates the two-step flow (form ↔ workshop picker) and the
 * create-booking submit (comp OR reservation-with-emailed-link).
 *
 * On success the modal reports *what* was created via `onCreated(outcome)`
 * and stops there — navigation/confirmation is `CreateBookingLauncher`'s job.
 */
import { useRef, useState } from 'react';

import { createCompBooking, CsrBookingError } from '../../../api/bookingsV2';
import { createReservation } from '../../../api/reservations';
import { usePermissions } from '../../../hooks/usePermissions';
import { Permission } from '../../../types/permissions';

import type { CreateBookingFieldsProps } from './CreateBookingModalFields';
import { type CreateBookingFormApi, useCreateBookingForm } from './CreateBookingModalForm';
import type { CreateBookingSuccess } from './CreateBookingModalHelpers';
import { CreateBookingModalShell } from './CreateBookingModalShell';
import { CreateBookingWorkshopPicker } from './CreateBookingWorkshopPicker';

export interface CreateBookingModalProps {
  onClose: () => void;
  /** Called once a booking is successfully created, with the outcome the
   *  launcher needs to close the modal, navigate, and confirm. */
  onCreated: (outcome: CreateBookingSuccess) => void;
}

/** Assemble the fields prop-bag consumed by `<CreateBookingModalFields />`. */
function buildFields(
  f: ReturnType<typeof useCreateBookingForm>,
  canComp: boolean,
  onOpenPicker: () => void,
): CreateBookingFieldsProps {
  return {
    workshop: f.workshop,
    onOpenPicker,
    fullName: f.fullName,
    setFullName: f.setFullName,
    email: f.email,
    setEmail: f.setEmail,
    tickets: f.tickets,
    setTickets: f.setTickets,
    setAllTickets: f.setAllTickets,
    mode: f.mode,
    setMode: f.setMode,
    reason: f.reason,
    setReason: f.setReason,
    canComp,
    money: f.money,
  };
}

async function submitComp(f: CreateBookingFormApi): Promise<CreateBookingSuccess> {
  const result = await createCompBooking({
    workshopId: f.workshop!.id,
    attendeeCount: f.tickets,
    reason: f.reason.trim(),
    customer: { email: f.email.trim(), fullName: f.fullName.trim() || undefined },
  });
  return { kind: 'comp', bookingId: result.bookingId, confirmationCode: result.confirmationCode };
}

async function submitReservation(f: CreateBookingFormApi): Promise<CreateBookingSuccess> {
  const email = f.email.trim();
  const result = await createReservation({
    workshopId: f.workshop!.id,
    ticketCount: f.tickets,
    customerName: f.fullName.trim(),
    customerEmail: email,
  });
  return {
    kind: 'reservation',
    expiresAt: result.expiresAt,
    // The duplicate path echoes no address, so fall back to what the CSR
    // typed — it is the same reservation either way.
    email: result.emailedTo ?? email,
    duplicate: Boolean(result.duplicate),
    // Undefined means the duplicate path, where the first submit sent it.
    emailed: result.emailed !== false,
  };
}

export default function CreateBookingModal({ onClose, onCreated }: CreateBookingModalProps) {
  const { hasPermission } = usePermissions();
  const canComp = hasPermission(Permission.WRITE_COMP_BOOKINGS);
  const f = useCreateBookingForm();
  const [view, setView] = useState<'form' | 'picker'>('form');
  // Belt-and-suspenders alongside the submit button's `disabled` state: a
  // double-click can fire twice before React re-renders the disabled button,
  // and this ref is checked synchronously so it can't.
  const submittingRef = useRef(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (submittingRef.current) return;
    if (f.validationError) {
      f.setError(f.validationError);
      return;
    }
    f.setError(null);
    submittingRef.current = true;
    f.setIsSubmitting(true);
    // Kept out of the try: once the create has succeeded, a throw from
    // `onCreated` (the launcher's navigate/toast handling) must not be
    // mistaken for a failed create — that shows "Failed to create booking"
    // for a booking that exists, and invites a duplicate comp on retry.
    let outcome: CreateBookingSuccess | null = null;
    try {
      outcome = f.mode === 'comp' ? await submitComp(f) : await submitReservation(f);
    } catch (err) {
      f.setError(
        err instanceof CsrBookingError || err instanceof Error
          ? err.message
          : 'Failed to create booking',
      );
    } finally {
      submittingRef.current = false;
      f.setIsSubmitting(false);
    }
    if (!outcome) return;
    try {
      onCreated(outcome);
    } catch (err) {
      // The create already succeeded — a bug in the launcher's own
      // post-create handling must not read as a failed create to the CSR.
      console.error('onCreated threw after a successful create', err);
    }
  };

  if (view === 'picker') {
    return (
      <CreateBookingWorkshopPicker
        onBack={() => setView('form')}
        onClose={onClose}
        onPick={(workshop) => {
          f.setWorkshop(workshop);
          setView('form');
        }}
      />
    );
  }

  return (
    <CreateBookingModalShell
      fields={buildFields(f, canComp, () => setView('picker'))}
      error={f.error}
      isSubmitting={f.isSubmitting}
      submitDisabled={f.isSubmitting || !!f.validationError}
      onClose={onClose}
      onSubmit={handleSubmit}
    />
  );
}
